import React, { useMemo } from 'react';

import EntityDropDownFilterContainerComponent from 'components/entityViewer/entitiesDropDownFilter/EntityDropDownFilterContainer';
import {
    useFacilityOptions,
    useStatusOptions,
} from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';

import {
    FILTER_QUERY_KEYS,
    PartnerPackagingPurchaseOrderFilterConfigArray,
} from './PartnerPackagingPurchaseOrderFilterConfig';

/**
 * Slim filter bar for partner packaging purchase orders.
 *
 * Facility options are whitelisted to the selected FBM account's facilityIDs when provided.
 *
 * @param {Object} props
 * @param {Object} [props.initialValues={}]
 * @param {(nextFilters: Object) => void} [props.onChange]
 * @param {number[]} [props.whitelistedFacilityIds]
 */
export function PartnerPackagingPurchaseOrderFilters({ initialValues = {}, onChange, whitelistedFacilityIds }) {
    const { data: statusOptions = [], isLoading: isLoadingStatuses } = useStatusOptions();
    const { data: facilityOptions = [], isLoading: isLoadingFacilities } = useFacilityOptions();

    const whitelistedFacilityIdSet = useMemo(() => {
        if (!Array.isArray(whitelistedFacilityIds) || whitelistedFacilityIds.length === 0) {
            return null;
        }
        return new Set(whitelistedFacilityIds.map((id) => Number(id)));
    }, [whitelistedFacilityIds]);

    const filteredFacilityOptions = useMemo(() => {
        if (!whitelistedFacilityIdSet) return facilityOptions;
        return facilityOptions.filter((option) => whitelistedFacilityIdSet.has(Number(option.value)));
    }, [facilityOptions, whitelistedFacilityIdSet]);

    const effectiveFilterConfigs = useMemo(
        () =>
            PartnerPackagingPurchaseOrderFilterConfigArray.map((cfg) => {
                const queryId = cfg.remoteOptions?.queryId;

                if (queryId === FILTER_QUERY_KEYS.STATUSES) {
                    return {
                        ...cfg,
                        options: statusOptions,
                        menuProps: {
                            ...cfg.menuProps,
                            isLoading: isLoadingStatuses,
                        },
                    };
                }

                if (queryId === FILTER_QUERY_KEYS.FACILITIES) {
                    return {
                        ...cfg,
                        options: filteredFacilityOptions,
                        menuProps: {
                            ...cfg.menuProps,
                            isLoading: isLoadingFacilities,
                        },
                    };
                }

                return cfg;
            }),
        [filteredFacilityOptions, isLoadingFacilities, isLoadingStatuses, statusOptions],
    );

    const handleFilterChange = (filterValues) => {
        if (onChange) {
            onChange(filterValues);
        }
    };

    return (
        <EntityDropDownFilterContainerComponent
            title="Packaging Purchase Order Filters"
            filterConfigs={effectiveFilterConfigs}
            initialValues={initialValues}
            onChange={handleFilterChange}
            autoApply
        />
    );
}
